import type { KeycloakTokens } from '../types/globals';
import { bxClearOfflineToken, bxLoadOfflineToken, bxSaveOfflineToken } from '@lib/bx24';

// ========== Config ==========
const KEYCLOAK_BASE = window.FZ_AUTH_BASE_URL;
const REALM = window.FZ_AUTH_REALM;
const CLIENT_ID = window.FZ_AUTH_CLIENT_ID;

// ========== Endpoints ==========
const tokenEndpoint = () => `${KEYCLOAK_BASE}/realms/${REALM}/protocol/openid-connect/token`;
const authzEndpoint = () => `${KEYCLOAK_BASE}/realms/${REALM}/protocol/openid-connect/auth`;
const revokeEndpoint = () => `${KEYCLOAK_BASE}/realms/${REALM}/protocol/openid-connect/revoke`;
const logoutEndpoint = () => `${KEYCLOAK_BASE}/realms/${REALM}/protocol/openid-connect/logout`;

// ========== Utils ==========
const b64url = (buf: ArrayBuffer | Uint8Array) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let str = btoa(String.fromCharCode(...bytes));
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randStr = (len = 64) => {
  const a = new Uint8Array(len);
  crypto.getRandomValues(a);
  return b64url(a).slice(0, len);
};

const sha256 = async (str: string) => {
  const e = new TextEncoder().encode(str);
  const h = await crypto.subtle.digest('SHA-256', e);
  return new Uint8Array(h);
};

function decodeJwt(token?: string | null): Record<string, any> | null {
  try {
    if (!token) return null;
    const [, payload] = token.split('.');
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base.length % 4 === 2 ? '==' : base.length % 4 === 3 ? '=' : '';
    return JSON.parse(atob(base + pad));
  } catch {
    return null;
  }
}

function siblingUrl(filename: string): string {
  const { origin, pathname } = window.location;
  const i = pathname.lastIndexOf('/');
  const dir = i >= 0 ? pathname.slice(0, i + 1) : '/';
  return `${origin}${dir}${filename}`;
}

function buildAuthWindowUrl(redirectUri: string, state: string, challenge: string): string {
  const url = new URL(authzEndpoint());
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email offline_access');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('response_mode', 'query');
  return url.toString();
}

function openAuthWindow(url: string): Window | null {
  const w = 520, h = 640;
  const left = Math.max(0, window.screenX + (window.outerWidth - w) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - h) / 2);
  return window.open(url, 'kc_bitrix_login', `width=${w},height=${h},left=${left},top=${top},resizable,scrollbars`);
}

// ========== Keycloak flows ==========
export async function kcLogin(): Promise<KeycloakTokens> {
  const state = randStr(16);
  const verifier = randStr(64);
  const challenge = await sha256(verifier).then(b64url);

  // Открываем дочернее окно браузера, в котором будет форма авторизации Keycloak
  const redirectUri = siblingUrl('auth_callback.html'); // <-- всегда рядом с index.html
  const authWindowUrl = buildAuthWindowUrl(redirectUri, state, challenge);
  const popup = openAuthWindow(authWindowUrl);
  if (!popup) throw new Error('Браузер заблокировал окно входа');

  const tok: KeycloakTokens = await new Promise((resolve, reject) => {
    const parentOrigin = window.location.origin;
    // Через 5 минут закрываем дочернее окно, прерываем процедуру авторизации
    const timeoutId = window.setTimeout(() => {
      cleanup();
      try { popup.close(); } catch { }
      reject(new Error('Login timeout'));
    }, 5 * 60 * 1000);
    // Мониторим закрытие дочернего окна
    const pollId = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error('Login popup closed'));
      }
    }, 2000);
    function cleanup() {
      window.removeEventListener('message', onMsg);
      clearTimeout(timeoutId);
      clearInterval(pollId);
    }

    // Это обработка ответного сообщения из дочернего окна
    async function onMsg(ev: MessageEvent) {
      cleanup();
      // приём только от нашего попапа и с нашего origin
      if (ev.origin !== parentOrigin) return;
      if (ev.source !== popup) return;
      const data = (ev && ev.data) || {};
      if (data.type !== 'kc:code') return;
      // сверяем state — ТОЛЬКО тот, который мы генерировали
      if (data.state !== state) return;

      // меняем code -> token тут, в родителе (верифайер в памяти)
      try {
        const requestBody = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: CLIENT_ID,
          code: String(data.code || ''),
          redirect_uri: redirectUri,
          code_verifier: verifier,
        });
        const resp = await fetch(tokenEndpoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: requestBody
        });
        if (!resp.ok) throw new Error('token_endpoint_error');
        const tok: KeycloakTokens = await resp.json();
        resolve(tok);
      } catch (e) {
        reject(e);
      } finally {
        try { popup?.close(); } catch { }
      }
    }
    window.addEventListener('message', onMsg);
  });

  await bxSaveOfflineToken(String(tok.refresh_token || ''));
  const probe = await bxLoadOfflineToken();
  if (!probe) throw new Error('Offline-токен не сохранился в entity');
  return tok;
}

export async function kcLogout() {
  const rt = await bxLoadOfflineToken();
  if (rt) {
    const revokeBody = new URLSearchParams({ client_id: CLIENT_ID, token: rt, token_type_hint: 'refresh_token' });
    try { await fetch(revokeEndpoint(), { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: revokeBody }); } catch { }
    const logoutBody = new URLSearchParams({ client_id: CLIENT_ID, refresh_token: rt });
    try { await fetch(logoutEndpoint(), { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: logoutBody }); } catch { }
  }
  await bxClearOfflineToken();
}

export async function kcGetAccessTokenFromOfflineToken(offlineToken: string): Promise<KeycloakTokens> {
  const requestBody = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: CLIENT_ID,
    refresh_token: offlineToken
  });
  const resp = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: requestBody
  });
  if (!resp.ok) throw new Error('Refresh with offline token failed');
  return await resp.json();
}

export function kcExtractUsernameFromTokens(tokens?: KeycloakTokens | null): string {
  const idc = tokens?.id_token ? decodeJwt(tokens.id_token) : null;
  const acc = tokens?.access_token ? decodeJwt(tokens.access_token) : null;
  const c = idc || acc || {};
  return c.preferred_username || c.email || c.name || 'Keycloak user';
}
